import _ from 'lodash';
import moment from 'moment';
import { handlebars, handlebarsSync, TResolveMediaSrc } from '../utils/handlebars';
import {
  formatDate,
  formatNumber,
  formatOccupancy,
  formatOccupancySeating,
  formatOfferDay,
  formatPercentage,
  formatPrice,
  formatTime,
  getTimezoneForCountry,
  intlLocaleForOfferLanguage,
} from '../utils/format';
import { htmlTemplate2Html, pdfTemplate2Html } from '../utils/print';
import { NO_PRICE } from '../pricelist/quickprice';
import { QuickPriceCalculatorTypeLister } from '../pricelist/quickprice_lister';
import { QuickPriceCalculatorTypeWidget } from '../pricelist/quickprice_widget';
import { allCountries } from '../utils/countries';

export type EWL_OfferVersionContentBlockType = 'LINE_ITEMS' | 'CANCELLATION_ITEMS' | 'PLAIN_HTML' | 'INPUT_DAYS' | 'CUSTOM_FORM';
export type EWL_OfferDocumentType = 'OFFER' | 'RESERVATION' | 'INVOICE' | 'CINVOICE' | 'CANCELLATION';
export type EWL_OfferRoomOccupancy = 'MORNING' | 'AFTERNOON' | 'FULLDAY';

export const mapOfferStatusToDocumentType = (offerStatus: string, offerVersionStatus: string): EWL_OfferDocumentType => {
  if (offerStatus === 'INVOICE' && offerVersionStatus === 'CANCELLED_CLIENT') return 'CINVOICE';
  if (offerVersionStatus === 'CANCELLED_CLIENT') return 'CANCELLATION';
  if (offerStatus === 'INVOICE' || offerStatus === 'CLOSED') return 'INVOICE';
  if (offerStatus === 'APPROVED' || offerStatus === 'REJECTED' || offerStatus === 'ACTIVE') return 'RESERVATION';
  if (offerStatus === 'PENDING' && offerVersionStatus === 'WAITFORCLIENTAPPROVAL') return 'RESERVATION';
  return 'OFFER';
};

interface IOffer {
  contentBlocks: IContentBlock[];
  agreedCancellationWaiveNet?: number | null;
  agreedCancellationWaiveGross?: number | null;
  manualCancellationWaiveNet?: number | null;
  manualCancellationWaiveGross?: number | null;
}

interface IContentBlock {
  type: EWL_OfferVersionContentBlockType;
  lineItems?: ILineItem[] | null;
}

interface ILineItem {
  count: number;
  priceItem: number;
  priceGross: number;
  priceNet: number;
  taxes: ITax[];
}

interface ITax {
  taxRateId: number;
  name: string;
  rate: number;
  price: number;
}

export interface OfferTemplateInput extends IOffer {
  documentType: EWL_OfferDocumentType;
  pageOptions: OfferTemplatePageOptions;
  includeEmptyLineItems: boolean;
  date: Date;
  seminarTitle?: string | null;
  startDate: string;
  endDate: string;
  expirationDate: Date;
  refCode: String;
  client: OfferTemplateClient;
  hotel: OfferTemplateHotel;
  hotelLogoUrl?: string | null;
  seminargoLogoBase64?: string | null;
  hotelUrl?: string | null;
  hotelAgbUrl?: string | null;
  seminargoAgbUrl?: string | null;
  clientType?: string | null;
  sender?: OfferTemplateSenderAddress | null;
  receiver?: OfferTemplateReceiverAddress | null;
  priceList?: OfferTemplatePriceList | null;
  serviceType?: OfferTemplateServiceType | null;
  contentBlocks: OfferTemplateContentBlock[];
  cancellationRules: OfferTemplateCancellationRule[];
  /** Offer-level waiver (0–1); templates use via first rule’s toleranceRateFormatted when rows have no tolerance. */
  cancellationToleranceRate?: number | null;
  downpaymentAmount?: number | null;
  downpaymentDueDate?: Date | null;
  priceDisplayMode?: 'net' | 'gross' | null;
  totalPriceNet: number;
  totalPriceGross: number;
  totalTax: number;
  totalTaxes: OfferTemplateTax[];
  personalMessage?: string | null;
  hotelMessage?: string | null;
  additionalInfo1?: string | null;
  additionalInfo2?: string | null;
  additionalInfo3?: string | null;
  contactPerson?: OfferTemplateContactPerson | null;
  hotelApproval?: OfferTemplateProcessAction | null;
  clientApproval?: OfferTemplateProcessAction | null;
  hotelReservation?: OfferTemplateProcessAction | null;
  /** Offer language (e.g. GraphQL `ELanguageCode`) for PDF cancellation copy — matches client-platform My Bookings. */
  language?: string | null;
  /** PDF filename pattern (from offer version or cancellation shell template). */
  filename?: string | null;
}

export interface OfferTemplateInputFormatted extends OfferTemplateInput {
  spaceId?: number | null;
  dateFormatted: string;
  startDateFormatted: string;
  endDateFormatted: string;
  expirationDateFormatted: string;
  client: OfferTemplateClientFormatted;
  hotel: OfferTemplateHotelFormatted;
  contentBlocks: OfferTemplateContentBlockFormatted[];
  cancellationRules: OfferTemplateCancellationRuleFormatted[];
  cancellationToleranceRateFormatted?: string | null;
  downpaymentAmountFormatted?: string | null;
  downpaymentDueDateFormatted?: string | null;
  totalPriceNetFormatted: string;
  totalPriceGrossFormatted: string;
  agreedCancellationWaiveNetFormatted?: string | null;
  agreedCancellationWaiveGrossFormatted?: string | null;
  manualCancellationWaiveNetFormatted?: string | null;
  manualCancellationWaiveGrossFormatted?: string | null;
  totalTaxFormatted: string;
  totalTaxes: OfferTemplateTaxFormatted[];
  hotelApproval?: OfferTemplateProcessActionFormatted | null;
  clientApproval?: OfferTemplateProcessActionFormatted | null;
  hotelReservation?: OfferTemplateProcessActionFormatted | null;
}

export interface OfferTemplateProcessAction {
  username?: string | null;
  name?: string | null;
  email?: string | null;
  date: Date;
}

export interface OfferTemplateProcessActionFormatted extends OfferTemplateProcessAction {
  dateFormatted: string;
  timeFormatted: string;
}

export interface OfferTemplateContactPerson {
  username: string;
  name: string;
  email: string;
}

export interface OfferTemplatePageOptions {
  title?: string | null;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  cssStyles?: string | null;
  headerTemplate?: string | null;
  footerTemplate?: string | null;
}

export interface OfferTemplateInputDayRoomOccupancy {
  room: number;
  occupancy?: string | null;
  occupancyTo?: string | null;
  occupancyFrom?: string | null;
  seating?: string | null;
}

export interface OfferTemplateInputDayAddon {
  sku: string;
  count?: number | null;
  header: string;
  details?: string | null;
}

export interface OfferTemplateInputDay {
  day: number;
  totalGuests: number;
  overnightGuests?: number | null;
  overnightRoomsEZ?: number | null;
  overnightRoomsDZ?: number | null;

  occupancy: OfferTemplateInputDayRoomOccupancy[];
  addons: OfferTemplateInputDayAddon[];
}

export interface OfferTemplateCancellationRule {
  daysToEvent: number;
  rate: number;
  toleranceRate?: number | null;
  minOvernight?: number | null;
  maxOvernight?: number | null;
  minTotalGuests?: number | null;
  maxTotalGuests?: number | null;
}

interface OfferTemplateCancellationRuleFormatted extends OfferTemplateCancellationRule {
  daysToEventFormatted: string;
  rateFormatted: string;
  toleranceRateFormatted?: string | null;
}

export interface OfferTemplateContentBlock extends IContentBlock {
  template: string;
  sequence: number;
  blockDescription?: string | null;
  inputDay?: OfferTemplateInputDay | null;
  lineItems?: OfferTemplateLineItem[] | null;
  formFields?: OfferTemplateFormField[] | null;
  cancellationRule?: OfferTemplateCancellationRule | null;
}

interface OfferTemplateContentBlockFormatted extends OfferTemplateContentBlock {
  inputDay?: OfferTemplateInputDayFormatted | null;
  lineItems: OfferTemplateLineItemFormatted[];
  cancellationRule?: OfferTemplateCancellationRuleFormatted | null;
  isFirstCancellation?: boolean;
  isLastCancellation?: boolean;
  isFirstLineItems?: boolean;
  isLastLineItems?: boolean;
}

export interface OfferTemplateClient {
  title?: string | null;
  firstname: string;
  lastname: string;
  company: string;
  email: string;
  address: string;
  city: string;
  zip: string;
  country: string;
  phone?: string | null | undefined;
  billingFirstname: string;
  billingLastname: string;
  billingCompany: string;
  billingAddress: string;
  billingCity: string;
  billingZip: string;
  billingCountry: string;
}

export interface OfferTemplateClientFormatted extends OfferTemplateClient {
  countryFormatted: string;
  billingCountryFormatted: string;
}

export interface OfferTemplateHotel {
  name: string;
  refCode: string;
  businessName?: string | null;
  businessAddress1?: string | null;
  businessAddress2?: string | null;
  businessAddress3?: string | null;
  businessAddress4?: string | null;
  businessZip?: string | null;
  businessCity?: string | null;
  businessCountry: string;
}

export interface OfferTemplateHotelFormatted extends OfferTemplateHotel {
  businessCountryFormatted: string;
}

export interface OfferTemplateAddress {
  businessName: string;
  businessAddress1: string;
  businessAddress2?: string | null;
  businessAddress3?: string | null;
  businessAddress4?: string | null;
  businessZip?: string | null;
  businessCity?: string | null;
  businessCountry: string;
  businessPhone?: string | null;
  businessEmail?: string | null;
  contactName?: string | null;
  comment?: string | null;
}

export interface OfferTemplateReceiverAddress extends OfferTemplateAddress {
  costCentre?: string | null;
  accountingArea?: string | null;

  attentionName?: string | null;
  bookingName?: string | null;
  vatId?: string | null;
  noVatIdReason?: string | null;
  taxId?: string | null;
  poId?: string | null;
}
export interface OfferTemplateSenderAddress extends OfferTemplateAddress {}

export interface OfferTemplatePriceList {
  name: string;
  refCode: string;
  version: string;
  currency: string;
}

export interface OfferTemplateServiceType {
  sku: string;
  name: string;
  header: string;
  details: string;
}

export interface OfferTemplateLineItem extends ILineItem {
  day: number;
  sku?: string | null;
  header: string;
  details?: string | null;
  components: OfferTemplatePriceComponent[];
  taxes: OfferTemplateTax[];
  sequence: number;
}

export interface OfferTemplateFormField {
  sequence: number;
  label: string;
  type: string;
  name: string;
  defaultValue?: string | null;
  value?: string | null;
  options?: string | null;
  required?: boolean | null;
  placeholder?: string | null;
  helpText?: string | null;
}

interface OfferTemplateLineItemFormatted extends OfferTemplateLineItem {
  dayFormatted: string;
  date: Date;
  dateFormatted: string;
  priceItemFormatted: string;
  priceItemGross: number;
  priceItemGrossFormatted: string;
  priceItemNet: number;
  priceItemNetFormatted: string;
  priceGrossFormatted: string;
  priceNetFormatted: string;
  components: OfferTemplatePriceComponentFormatted[];
  taxes: OfferTemplateTaxFormatted[];
}

interface OfferTemplateInputDayFormatted extends OfferTemplateInputDay {
  dayFormatted: string;
  date: Date;
  dateFormatted: string;
  occupancy: OfferTemplateInputDayRoomOccupancyFormatted[];
  addons: OfferTemplateInputDayAddonFormatted[];
}

interface OfferTemplateInputDayRoomOccupancyFormatted extends OfferTemplateInputDayRoomOccupancy {
  occupancyFormatted: string;
  seatingFormatted: string;
}

interface OfferTemplateInputDayAddonFormatted extends OfferTemplateInputDayAddon {
  count: number;
  sku: string;
  header: string;
  details: string;
}

export interface OfferTemplateTax extends ITax {}

interface OfferTemplateTaxFormatted extends OfferTemplateTax {
  rateFormatted: string;
  ratePercent: number;
  ratePercentFormatted: string;
  priceFormatted: string;
}

export interface OfferTemplatePriceComponent {
  name: string;
  price: number;
}

interface OfferTemplatePriceComponentFormatted extends OfferTemplatePriceComponent {
  priceFormatted: string;
}

export interface IOfferTemplate {
  name: string;
  includeEmptyLineItems: boolean;
  filename?: string | null;
  title?: string | null;
  cssStyles?: string | null;
  headerTemplate?: string | null;
  footerTemplate?: string | null;
  marginTop: number;
  marginBottom: number;
  marginLeft: number,
  marginRight: number;
  contentBlocks: {
    sequence: number;
    template: string;
    type: EWL_OfferVersionContentBlockType;
    blockDescription?: string | null;
    formFields?: {
      name: string;
      label: string;
      defaultValue?: string | null;
      type: string;
      required?: boolean | null;
      sequence: number;
    }[] | null;
  }[];
}

export const getFullEventDayRange = (contentBlocks: { lineItems: { day: number }[] }[]) => {
  const allDays = contentBlocks.reduce<number[]>((agg, cb) => [...agg, ...cb.lineItems.map(li => li.day)], []);
  const maxInputDay = Math.max(...allDays);
  if (maxInputDay === -Infinity) return allDays.includes(-1) ? [-1] : [];
  const eventDays = [...(allDays.includes(-1) ? [-1] : []), ...[...Array(maxInputDay + 1)].map((v, i) => i)];
  return eventDays;
};

export const getItemCountForLookupCodes = (
  day: number,
  lookupCodes: string[],
  contentBlocks: { lineItems: { day: number; count: number; sku?: string | null }[] }[],
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
) => {
  const skus = qp.getSKUsForLookupCodes(lookupCodes);
  return contentBlocks.reduce(
    (agg, cb) => agg + cb.lineItems.filter(li => li.day === day && li.sku && skus.includes(li.sku)).reduce((agg, li) => agg + li.count, 0),
    0,
  );
};

export const calculateTotalByLineItems = (lineItems: ILineItem[]) => {
  let totalPriceNet = 0;
  let totalPriceGross = 0;
  const totalTaxes = [] as ITax[];
  for (const li of lineItems.filter(li => li.count > 0)) {
    if (totalPriceNet === NO_PRICE || li.priceNet === NO_PRICE) totalPriceNet = NO_PRICE;
    else totalPriceNet += li.priceNet;
    if (totalPriceGross === NO_PRICE || li.priceGross === NO_PRICE) totalPriceGross = NO_PRICE;
    else totalPriceGross += li.priceGross;
    for (const tax of li.taxes || []) {
      const existingTax = totalTaxes.find(t => t.name === tax.name);
      if (existingTax) {
        if (existingTax.price === NO_PRICE || tax.price === NO_PRICE) existingTax.price = NO_PRICE;
        else existingTax.price += tax.price;
      } else {
        totalTaxes.push({ ...tax });
      }
    }
  }

  return {
    totalPriceNet,
    totalPriceGross,
    totalTax: totalPriceGross === NO_PRICE || totalPriceNet === NO_PRICE ? NO_PRICE : totalPriceGross - totalPriceNet,
    totalTaxes,
  };
};

export const calculateTotalByContentBlocks = (contentBlocks: IContentBlock[]) => {
  let totalPriceNet = 0;
  let totalPriceGross = 0;
  const totalTaxes = [] as ITax[];
  for (const cb of contentBlocks) {
    if (!cb.lineItems) continue;
    const r = calculateTotalByLineItems(cb.lineItems);
    if (totalPriceNet === NO_PRICE || r.totalPriceNet === NO_PRICE) totalPriceNet = NO_PRICE;
    else totalPriceNet += r.totalPriceNet;
    if (totalPriceGross === NO_PRICE || r.totalPriceGross === NO_PRICE) totalPriceGross = NO_PRICE;
    else totalPriceGross += r.totalPriceGross;
    for (const tax of r.totalTaxes || []) {
      const existingTax = totalTaxes.find(t => t.name === tax.name);
      if (existingTax) {
        if (existingTax.price === NO_PRICE || tax.price === NO_PRICE) existingTax.price = NO_PRICE;
        else existingTax.price += tax.price;
      } else {
        totalTaxes.push({ ...tax });
      }
    }
  }

  return {
    totalPriceNet,
    totalPriceGross,
    totalTax: totalPriceGross === NO_PRICE || totalPriceNet === NO_PRICE ? NO_PRICE : totalPriceGross - totalPriceNet,
    totalTaxes,
  };
};

export const calculateTotals = (offer: IOffer) => {
  let { totalPriceNet, totalPriceGross, totalTaxes } = calculateTotalByContentBlocks(offer.contentBlocks.filter(cb => cb.type === 'LINE_ITEMS'));
  let {
    totalPriceNet: cancellationFeeNet,
    totalPriceGross: cancellationFeeGross,
    totalTaxes: cancellationTaxes,
  } = calculateTotalByContentBlocks(offer.contentBlocks.filter(cb => cb.type === 'CANCELLATION_ITEMS'));

  if (cancellationFeeNet > 0) {
    const cancellationWaiveGross = (offer.agreedCancellationWaiveGross || 0) + (offer.manualCancellationWaiveGross || 0);
    const cancellationWaiveNet = (offer.agreedCancellationWaiveNet || 0) + (offer.manualCancellationWaiveNet || 0);

    if (cancellationWaiveNet > 0) {
      if (cancellationWaiveNet < cancellationFeeNet) {
        const finalCancellationFeeNet = cancellationFeeNet - cancellationWaiveNet;
        const ratio = finalCancellationFeeNet / cancellationFeeNet;

        cancellationFeeGross = cancellationFeeGross * ratio;
        cancellationFeeNet = cancellationFeeNet * ratio;
        for (const ctax of cancellationTaxes) {
          ctax.price = ctax.price * ratio;
        }
      } else {
        cancellationFeeGross = 0;
        cancellationFeeNet = 0;
        cancellationTaxes.splice(0, cancellationTaxes.length);
      }
    } else if (cancellationWaiveGross > 0) {
      if (cancellationWaiveGross < cancellationFeeGross) {
        const finalCancellationFeeGross = cancellationFeeGross - cancellationWaiveGross;
        const ratio = finalCancellationFeeGross / cancellationFeeGross;

        cancellationFeeGross = cancellationFeeGross * ratio;
        cancellationFeeNet = cancellationFeeNet * ratio;
        for (const ctax of cancellationTaxes) {
          ctax.price = ctax.price * ratio;
        }
      } else {
        cancellationFeeGross = 0;
        cancellationFeeNet = 0;
        cancellationTaxes.splice(0, cancellationTaxes.length);
      }
    }

    totalPriceNet = totalPriceNet === NO_PRICE ? NO_PRICE : totalPriceNet + cancellationFeeNet;
    totalPriceGross = totalPriceGross === NO_PRICE ? NO_PRICE : totalPriceGross + cancellationFeeGross;

    for (const ctax of cancellationTaxes) {
      const existingTax = totalTaxes.find(t => t.name === ctax.name);
      if (existingTax) {
        if (existingTax.price === NO_PRICE || ctax.price === NO_PRICE) existingTax.price = NO_PRICE;
        else existingTax.price += ctax.price;
      } else {
        totalTaxes.push({ ...ctax });
      }
    }
  }
  return {
    totalPriceNet,
    totalPriceGross,
    totalTax: totalPriceGross === NO_PRICE || totalPriceNet === NO_PRICE ? NO_PRICE : totalPriceGross - totalPriceNet,
    totalTaxes,
    cancellationFeeNet,
    cancellationFeeGross,
  };
};

function createOfferPdfFormatters(intlLocale: string) {
  return {
    formatPrice: (price: number, priceList?: OfferTemplatePriceList | null) =>
      price === NO_PRICE ? '' : formatPrice(price, intlLocale, (priceList && priceList.currency) || 'EUR'),
    formatNumber: (n: number, precision: number = 0) => formatNumber(n, intlLocale, precision),
    formatPercentage: (p: number) => formatPercentage(p, intlLocale),
    formatDate: (date: Date, timeZone: string = 'Europe/Vienna') => formatDate(date, intlLocale, timeZone),
    formatTime: (date: Date, timeZone: string = 'Europe/Vienna') => formatTime(date, intlLocale, timeZone),
  };
}

export const generateDummyOfferTemplateInput = (
  pageOptions: OfferTemplatePageOptions,
  contentBlocks: OfferTemplateContentBlock[],
): OfferTemplateInput => {
  const $f = createOfferPdfFormatters('de-AT');
  const offer: OfferTemplateInput = {
    documentType: 'OFFER',
    pageOptions: pageOptions,
    includeEmptyLineItems: false,
    date: new Date(),
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(2, 'days').format('YYYY-MM-DD'),
    expirationDate: moment(new Date()).add(3, 'days').toDate(),
    seminarTitle: 'Musterseminar',
    refCode: 'MUSTER-001',
    additionalInfo1: 'Zusatzinfo 1',
    additionalInfo2: 'Zusatzinfo 2',
    additionalInfo3: 'Zusatzinfo 3',
    hotelUrl: 'https://www.musterhotel.de',
    personalMessage: 'Persönliche Nachricht vom Hotel an den Kunden Beispiel',
    client: {
      title: 'Dr.',
      firstname: 'Max',
      lastname: 'Mustermann',
      company: 'Musterfirma',
      address: 'Musterstraße 1',
      city: 'Musterstadt',
      zip: '12345',
      country: 'Deutschland',
      phone: '+43 123 123 123',
      billingFirstname: 'Max',
      billingLastname: 'Mustermann',
      billingCompany: 'Musterfirma',
      billingAddress: 'Musterstraße 1',
      billingCity: 'Musterstadt',
      billingZip: '12345',
      billingCountry: 'Deutschland',
      email: 'max.mustermann@example.com',
    },
    hotel: {
      name: 'Musterhotel',
      refCode: 'M123123',
      businessName: 'Musterhotel',
      businessAddress1: 'Musterstraße 1',
      businessZip: '12345',
      businessCity: 'Musterstadt',
      businessCountry: 'Deutschland',
    },
    sender: {
      businessName: 'Musterhotel',
      businessAddress1: 'Musterstraße 1',
      businessZip: '12345',
      businessCity: 'Musterstadt',
      businessCountry: 'Deutschland',
      businessEmail: 'test@musterhotel.de',
      businessPhone: '+49 124 3434344',
      contactName: 'Klaus Rezeption',
    },
    receiver: {
      businessName: 'Musterfirma',
      businessAddress1: 'Musterstraße 1',
      businessZip: '12345',
      businessCity: 'Musterstadt',
      businessCountry: 'Deutschland',
      contactName: 'Karl Bucher',
      accountingArea: 'BK-001',
      costCentre: 'KS-002',
    },
    priceList: {
      name: 'Preisliste',
      version: '1.0',
      refCode: 'P123123',
      currency: 'EUR',
    },
    serviceType: {
      name: 'Service-Variante 1',
      sku: 'S123123',
      header: 'Service-Variante 1',
      details: 'Service Variante 1 Details',
    },
    cancellationRules: [
      {
        daysToEvent: 60,
        rate: 0.3,
      },
      {
        daysToEvent: 42,
        rate: 0.5,
      },
    ],
    language: 'de',
    contentBlocks: [...contentBlocks],
    priceDisplayMode: 'gross',
    totalPriceNet: 0,
    totalPriceGross: 0,
    agreedCancellationWaiveNet: 10,
    agreedCancellationWaiveGross: 12,
    manualCancellationWaiveNet: 5,
    manualCancellationWaiveGross: 6,
    totalTax: 0,
    totalTaxes: [
      {
        taxRateId: 1,
        name: 'Standardsteuersatz',
        price: 50.5,
        rate: 0.2,
      },
    ],
  };

  const inputDaysContentBlock = offer.contentBlocks.find(cb => cb.type === 'INPUT_DAYS' && (!cb.lineItems || cb.lineItems.length === 0));
  const inputDaysContentBlockIndex = offer.contentBlocks.findIndex(cb => cb.type === 'INPUT_DAYS' && (!cb.lineItems || cb.lineItems.length === 0));

  if (inputDaysContentBlock) {
    const inputDaysBlocks = [];
    for (let i = -1; i < 3; i++) {
      const dayProd = {
        day: i,
        dayFormatted: formatOfferDay(i),
        date: moment.utc(offer.startDate, 'YYYY-MM-DD').add(i, 'days').toDate(),
        dateFormatted: $f.formatDate(moment.utc(offer.startDate, 'YYYY-MM-DD').add(i, 'days').toDate()),
        totalGuests: 1,
        overnightGuests: 1,
        occupancy: [
          {
            room: 1,
            occupancy: 'MORNING',
            seating: 'UFORM',
          },
        ],
        addons: [
          {
            sku: 'P123123',
            header: 'Zusatzleistung 1',
            details: 'Zusatzleistung 1 Details',
            count: 1,
          },
        ],
      };

      const inputDaysContentBlockClone = _.cloneDeep(inputDaysContentBlock);
      inputDaysContentBlockClone.inputDay = dayProd;
      inputDaysBlocks.push(inputDaysContentBlockClone);
    }

    offer.contentBlocks.splice(inputDaysContentBlockIndex, 1, ...inputDaysBlocks);
  }

  const lineItemsContentBlock = offer.contentBlocks.find(cb => cb.type === 'LINE_ITEMS' && (!cb.lineItems || cb.lineItems.length === 0));
  const lineItemsContentBlockIndex = offer.contentBlocks.findIndex(cb => cb.type === 'LINE_ITEMS' && (!cb.lineItems || cb.lineItems.length === 0));

  if (lineItemsContentBlock) {
    const lineItemsBlocks = [];
    for (let i = -1; i < 3; i++) {
      const dayProd = {
        day: i,
        dayFormatted: formatOfferDay(i),
        date: moment.utc(offer.startDate, 'YYYY-MM-DD').add(i, 'days').toDate(),
        dateFormatted: $f.formatDate(moment.utc(offer.startDate, 'YYYY-MM-DD').add(i, 'days').toDate()),
        count: 1,
        priceItem: 100.15,
        priceItemFormatted: $f.formatPrice(100.15, offer.priceList),
        priceNet: 100.15,
        priceNetFormatted: $f.formatPrice(100.15, offer.priceList),
        priceGross: 119.15,
        priceGrossFormatted: $f.formatPrice(119.15, offer.priceList),
        components: [
          {
            name: 'Standardsteuersatz',
            price: 10.5,
          },
        ],
        taxes: [
          {
            taxRateId: 1,
            name: 'Standardsteuersatz',
            price: 10.5,
            rate: 0.2,
          },
        ],
      };

      const lineItemsContentBlockClone = _.cloneDeep(lineItemsContentBlock);
      lineItemsContentBlockClone.lineItems = [
        {
          ..._.cloneDeep(dayProd),
          sku: 'P123123',
          header: 'Produkt 1',
          details: 'Produkt Details 1',
          sequence: 1,
        },
        {
          ..._.cloneDeep(dayProd),
          sku: 'P123124',
          header: 'Produkt 2',
          details: 'Produkt Details 2',
          sequence: 2,
        },
      ];
      lineItemsBlocks.push(lineItemsContentBlockClone);
    }

    offer.contentBlocks.splice(lineItemsContentBlockIndex, 1, ...lineItemsBlocks);
  }

  const cancellationItemsContentBlock = offer.contentBlocks.find(
    cb => cb.type === 'CANCELLATION_ITEMS' && (!cb.lineItems || cb.lineItems.length === 0),
  );
  const cancellationItemsContentBlockIndex = offer.contentBlocks.findIndex(
    cb => cb.type === 'CANCELLATION_ITEMS' && (!cb.lineItems || cb.lineItems.length === 0),
  );

  if (cancellationItemsContentBlock) {
    const cancellationItemsContentBlockClone = _.cloneDeep(cancellationItemsContentBlock);
    cancellationItemsContentBlockClone.cancellationRule = {
      daysToEvent: 60,
      rate: 0.3,
    };
    cancellationItemsContentBlockClone.lineItems = [];

    for (let i = -1; i < 3; i++) {
      const dayProd = {
        day: i,
        dayFormatted: formatOfferDay(i),
        date: moment.utc(offer.startDate, 'YYYY-MM-DD').add(i, 'days').toDate(),
        dateFormatted: $f.formatDate(moment.utc(offer.startDate, 'YYYY-MM-DD').add(i, 'days').toDate()),
        count: 1,
        priceItem: 33.15,
        priceItemFormatted: $f.formatPrice(33.15, offer.priceList),
        priceNet: 33.15,
        priceNetFormatted: $f.formatPrice(33.15, offer.priceList),
        priceGross: 40.15,
        priceGrossFormatted: $f.formatPrice(40.15, offer.priceList),
        components: [
          {
            name: 'Standardsteuersatz',
            price: 3.5,
          },
        ],
        taxes: [
          {
            taxRateId: 1,
            name: 'Standardsteuersatz',
            price: 3.5,
            rate: 0.2,
          },
        ],
      };
      cancellationItemsContentBlockClone.lineItems.push({
        ..._.cloneDeep(dayProd),
        sku: 'P123123',
        header: 'Produkt 1',
        details: 'Produkt Details 1',
        sequence: 1,
      });
    }

    offer.contentBlocks.splice(cancellationItemsContentBlockIndex, 1, cancellationItemsContentBlockClone);
  }

  const { totalPriceNet, totalPriceGross, totalTax, totalTaxes } = calculateTotals(offer);
  offer.totalPriceNet = totalPriceNet;
  offer.totalPriceGross = totalPriceGross;
  offer.totalTax = totalTax;
  offer.totalTaxes = totalTaxes;
  return offer;
};

export const applyFormattingToCountry = (country: string) => {
  const countryName = allCountries.find(c => c.code === country)?.name;
  if (countryName) {
    return `${country} - ${countryName}`;
  }
  return country;
};

/**
 * HTML for the cancellation section — mirrors `OfferListItem` (My Bookings) logic and `common.json` DE/EN strings.
 * Uses offer-level `cancellationToleranceRate` only for the waiver line, same as the bookings UI.
 */
export function buildBookingsPageCancellationPolicyHtml(
  rules: OfferTemplateCancellationRule[],
  cancellationToleranceRate: number | null | undefined,
  language: string | null | undefined,
): string {
  const lang = (language || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
  const copy =
    lang === 'en'
      ? {
        title: 'Terms and Conditions',
        introLine: 'The following cancellation conditions apply to reservations:',
          daysLine: (days: number, ratePct: string) =>
            `${days} days before the event begins, a cancellation fee of ${ratePct}% applies.`,
          overnight: 'Overnight guests',
          seminar: 'Seminar guests',
          toleranceLine: (pct: string) =>
            `${pct}% of the agreed cancellation waiver will be taken into account.`,
        }
      : {
        title: 'Stornobedingungen:',
        introLine: 'Folgende Stornobedingungen sind bei einer Reservierung geltend:',
          daysLine: (days: number, ratePct: string) =>
            `Ab ${days} Tagen vor Veranstaltungsbeginn fallen ${ratePct} % Stornokosten an.`,
          overnight: 'Übernachtungsgäste',
          seminar: 'Seminargäste',
          toleranceLine: (pct: string) => `Es werden ${pct} % vereinbarter Stornoverzicht berücksichtigt.`,
        };

  if (!rules.length) {
    return '';
  }

  const parts: string[] = [`<h4>${copy.title}</h4>`];

  const hasCategories = rules.some(
    r => r.minOvernight != null || r.maxOvernight != null || r.minTotalGuests != null || r.maxTotalGuests != null,
  );

  const ruleP = (r: OfferTemplateCancellationRule) => {
    const ratePct = String(Math.round(r.rate * 100));
    return `<p>${copy.daysLine(r.daysToEvent, ratePct)}</p>`;
  };

  if (hasCategories) {
    const overnight = rules.filter(r => r.minOvernight != null || r.maxOvernight != null);
    const seminar = rules.filter(r => r.minTotalGuests != null || r.maxTotalGuests != null);
    if (overnight.length > 0) {
      parts.push(`<p><strong>${copy.overnight}</strong></p>`);
      overnight.forEach(r => parts.push(ruleP(r)));
    }
    if (seminar.length > 0) {
      parts.push(
        overnight.length > 0
          ? `<p style="margin-top:0.35em"><strong>${copy.seminar}</strong></p>`
          : `<p><strong>${copy.seminar}</strong></p>`,
      );
      seminar.forEach(r => parts.push(ruleP(r)));
    }
  } else {
    _.sortBy(rules, 'daysToEvent').forEach(r => parts.push(ruleP(r)));
  }

  if (cancellationToleranceRate != null && cancellationToleranceRate > 0) {
    parts.push(`<p>${copy.toleranceLine(String(Math.round(cancellationToleranceRate * 100)))}</p>`);
  }

  return parts.join('\n');
}

export function applyFormattingToInput(spaceId: number | null | undefined, template: OfferTemplateInput): OfferTemplateInputFormatted {
  // Get timezone from hotel's country
  const timeZone = getTimezoneForCountry(template.hotel?.businessCountry);
  const $f = createOfferPdfFormatters(intlLocaleForOfferLanguage(template.language));

  const rawCancellationRules = template.cancellationRules || [];

  const formatCancellationRuleRow = (
    cr: OfferTemplateCancellationRule,
    mergeOfferTolerance: boolean,
  ): OfferTemplateCancellationRuleFormatted => {
    const offerTol = template.cancellationToleranceRate;
    const tol =
      cr.toleranceRate != null && cr.toleranceRate > 0
        ? cr.toleranceRate
        : mergeOfferTolerance && offerTol != null && offerTol >= 0
          ? offerTol
          : null;
    return {
      ...cr,
      daysToEventFormatted: $f.formatNumber(cr.daysToEvent),
      rateFormatted: $f.formatPercentage(cr.rate),
      toleranceRateFormatted: tol != null ? $f.formatPercentage(tol) : null,
    };
  };

  const cancellationRules = rawCancellationRules.map((cr, idx) => formatCancellationRuleRow(cr, idx === 0));

  const inputFormatted: OfferTemplateInputFormatted = {
    spaceId,
    ...template,
    dateFormatted: $f.formatDate(template.date, timeZone),
    startDateFormatted: $f.formatDate(new Date(template.startDate), timeZone),
    endDateFormatted: $f.formatDate(new Date(template.endDate), timeZone),
    expirationDateFormatted: $f.formatDate(template.expirationDate, timeZone),
    client: {
      ...template.client,
      countryFormatted: applyFormattingToCountry(template.client.country),
      billingCountryFormatted: applyFormattingToCountry(template.client.billingCountry),
    },
    hotel: {
      ...template.hotel,
      businessCountryFormatted: applyFormattingToCountry(template.hotel.businessCountry),
    },
    cancellationRules,
    cancellationToleranceRateFormatted:
      template.cancellationToleranceRate != null && template.cancellationToleranceRate >= 0
        ? $f.formatPercentage(template.cancellationToleranceRate)
        : null,
    downpaymentAmountFormatted: template.downpaymentAmount ? $f.formatPrice(template.downpaymentAmount, template.priceList) : null,
    downpaymentDueDateFormatted: template.downpaymentDueDate ? $f.formatDate(template.downpaymentDueDate, timeZone) : null,
    contentBlocks: (template.contentBlocks || []).map(cb => ({
      ...cb,
      inputDay: {
        overnightRoomsEZ: cb.inputDay?.overnightRoomsEZ ?? null,
        overnightRoomsDZ: cb.inputDay?.overnightRoomsDZ ?? null,
        day: cb.inputDay?.day || 0,
        totalGuests: cb.inputDay?.totalGuests || 0,
        overnightGuests: cb.inputDay?.overnightGuests || 0,
        occupancy: (cb.inputDay?.occupancy || []).map(o => ({
          ...o,
          room: o.room || 0,
          occupancy: o.occupancy || '',
          occupancyFormatted: formatOccupancy(o.occupancy || ''),
          seating: o.seating || '',
          seatingFormatted: formatOccupancySeating(o.seating || ''),
        })),
        addons: (cb.inputDay?.addons || []).map(a => ({
          ...a,
          sku: a.sku || '',
          header: a.header || '',
          details: a.details || '',
          count: a.count || 0,
        })),
        dayFormatted: formatOfferDay(cb.inputDay?.day || 0),
        date: moment.utc(template.startDate, 'YYYY-MM-DD')
          .add(cb.inputDay?.day || 0, 'days')
          .toDate(),
        dateFormatted: $f.formatDate(
          moment.utc(template.startDate, 'YYYY-MM-DD')
            .add(cb.inputDay?.day || 0, 'days')
            .toDate(),
          timeZone,
        ),
      },
      lineItems: (cb.lineItems || [])
        .filter(li => template.includeEmptyLineItems || li.priceNet !== 0)
        .map(li => ({
          ...li,
          dayFormatted: formatOfferDay(li.day),
          date: moment.utc(template.startDate, 'YYYY-MM-DD').add(li.day, 'days').toDate(),
          dateFormatted: $f.formatDate(moment.utc(template.startDate, 'YYYY-MM-DD').add(li.day, 'days').toDate(), timeZone),
          priceItemFormatted: $f.formatPrice(li.priceItem, template.priceList),
          priceItemGross: li.priceGross === NO_PRICE ? NO_PRICE : (li.priceGross / (li.count || 1)),
          priceItemGrossFormatted: $f.formatPrice(li.priceGross === NO_PRICE ? NO_PRICE : (li.priceGross / (li.count || 1)), template.priceList),
          priceItemNet: li.priceNet === NO_PRICE ? NO_PRICE : (li.priceNet / (li.count || 1)),
          priceItemNetFormatted: $f.formatPrice(li.priceNet === NO_PRICE ? NO_PRICE : (li.priceNet / (li.count || 1)), template.priceList),
          priceNetFormatted: $f.formatPrice(li.priceNet, template.priceList),
          priceGrossFormatted: $f.formatPrice(li.priceGross, template.priceList),
          components: (li.components || []).map(c => ({
            ...c,
            priceFormatted: $f.formatPrice(c.price),
          })),
          taxes: (li.taxes || []).map(t => ({
            ...t,
            rateFormatted: $f.formatNumber(t.rate, 2),
            ratePercent: t.rate * 100.0,
            ratePercentFormatted: $f.formatPercentage(t.rate),
            priceFormatted: $f.formatPrice(t.price),
          })),
        })),
      cancellationRule: cb.cancellationRule
        ? {
            ...cb.cancellationRule,
            daysToEventFormatted: $f.formatNumber(cb.cancellationRule.daysToEvent),
            rateFormatted: $f.formatPercentage(cb.cancellationRule.rate),
            toleranceRateFormatted: cb.cancellationRule.toleranceRate
              ? $f.formatPercentage(cb.cancellationRule.toleranceRate)
              : null,
          }
        : cb.cancellationRule,
    })),
    totalPriceNetFormatted: $f.formatPrice(template.totalPriceNet, template.priceList),
    totalPriceGrossFormatted: $f.formatPrice(template.totalPriceGross, template.priceList),
    agreedCancellationWaiveGrossFormatted: template.agreedCancellationWaiveGross
      ? $f.formatPrice(template.agreedCancellationWaiveGross, template.priceList)
      : null,
    agreedCancellationWaiveNetFormatted: template.agreedCancellationWaiveNet
      ? $f.formatPrice(template.agreedCancellationWaiveNet, template.priceList)
      : null,
    manualCancellationWaiveGrossFormatted: template.manualCancellationWaiveGross
      ? $f.formatPrice(template.manualCancellationWaiveGross, template.priceList)
      : null,
    manualCancellationWaiveNetFormatted: template.manualCancellationWaiveNet
      ? $f.formatPrice(template.manualCancellationWaiveNet, template.priceList)
      : null,
    totalTaxFormatted: $f.formatPrice(template.totalTax, template.priceList),
    totalTaxes: (template.totalTaxes || []).map(t => ({
      ...t,
      rateFormatted: $f.formatNumber(t.rate, 2),
      ratePercent: t.rate * 100.0,
      ratePercentFormatted: $f.formatPercentage(t.rate),
      priceFormatted: $f.formatPrice(t.price),
    })),
    hotelApproval: template.hotelApproval ? {
      ...template.hotelApproval,
      dateFormatted: $f.formatDate(template.hotelApproval.date, timeZone),
      timeFormatted: $f.formatTime(template.hotelApproval.date, timeZone),
    } : null,
    clientApproval: template.clientApproval ? {
      ...template.clientApproval,
      dateFormatted: $f.formatDate(template.clientApproval.date, timeZone),
      timeFormatted: $f.formatTime(template.clientApproval.date, timeZone),
    } : null,
    hotelReservation: template.hotelReservation ? {
      ...template.hotelReservation,
      dateFormatted: $f.formatDate(template.hotelReservation.date, timeZone),
      timeFormatted: $f.formatTime(template.hotelReservation.date, timeZone),
    } : null,
  };

  // Set isFirstCancellation and isLastCancellation flags
  const cancellationBlocks = inputFormatted.contentBlocks.filter(cb => cb.type === 'CANCELLATION_ITEMS');
  cancellationBlocks.forEach((cb, index) => {
    cb.isFirstCancellation = index === 0;
    cb.isLastCancellation = index === cancellationBlocks.length - 1;
  });

  // Set isFirstLineItems and isLastLineItems flags
  const lineItemsBlocks = inputFormatted.contentBlocks.filter(cb => cb.type === 'LINE_ITEMS');
  lineItemsBlocks.forEach((cb, index) => {
    cb.isFirstLineItems = index === 0;
    cb.isLastLineItems = index === lineItemsBlocks.length - 1;
  });

  return inputFormatted;
}

/**
 * Reservation confirmation templates omit the hotel message block; inject personalMessage before customer message / cancellation.
 * Refreshes markup when a personalMessage block already exists (e.g. offer PDF samples).
 */
export function ensureHotelMessageContentBlock(offerInput: OfferTemplateInput): void {
  const personalMessage = `${offerInput.personalMessage || ''}`.trim();
  if (!personalMessage) return;

  const lang = `${offerInput.language || ''}`.trim().toLowerCase();
  const isEn = lang === 'en' || lang.startsWith('en-') || lang.startsWith('en_');
  const title = isEn ? 'Message from the Hotel' : 'Nachricht vom Hotel';
  const template = `<div class="container">
  {{#if personalMessage}}
  <div class="hotel-message">
    <h4>${title}</h4>
    <p>{{personalMessage}}</p>
  </div>
  {{/if}}
</div>`;

  const existingIdx = offerInput.contentBlocks.findIndex(cb => (cb.template || '').includes('{{personalMessage}}'));
  if (existingIdx >= 0) {
    offerInput.contentBlocks[existingIdx].template = template;
    return;
  }

  const block: OfferTemplateContentBlock = {
    type: 'PLAIN_HTML',
    template,
    sequence: 0,
    blockDescription: null,
    formFields: [],
    lineItems: [],
  };

  const customerMsgIdx = offerInput.contentBlocks.findIndex(cb => (cb.template || '').includes('{{hotelMessage}}'));
  if (customerMsgIdx >= 0) {
    offerInput.contentBlocks.splice(customerMsgIdx, 0, block);
    return;
  }

  const cancellationIdx = offerInput.contentBlocks.findIndex(
    cb =>
      String(cb.type) === 'CANCELLATION_ITEMS' ||
      (cb.template || '').includes('cancellationRules') ||
      (cb.template || '').includes('cancellation-section'),
  );
  if (cancellationIdx >= 0) {
    offerInput.contentBlocks.splice(cancellationIdx, 0, block);
    return;
  }

  offerInput.contentBlocks.push(block);
}

/**
 * Offer version PDFs use snapshotted content blocks; inject customer message before cancellation (SM-2452).
 * Always refreshes the block markup so spacing is applied even when a hotelMessage block already exists.
 */
export function ensureCustomerMessageContentBlock(offerInput: OfferTemplateInput): void {
  const hotelMessage = `${offerInput.hotelMessage || ''}`.trim();
  if (!hotelMessage) return;

  const lang = `${offerInput.language || ''}`.trim().toLowerCase();
  const isEn = lang === 'en' || lang.startsWith('en-') || lang.startsWith('en_');
  const title = isEn ? 'Message from the Customer' : 'Nachricht vom Kunden';
  // Fixed-height spacer: PDF engines often collapse margin/padding between adjacent .container blocks
  // Inline resets beat snapshotted .customer-message box styles (gray bg / purple border)
  const template = `<div class="container">
  {{#if hotelMessage}}
  <div class="customer-message" style="background:transparent;border:none;padding:0;margin:0;">
    <h4 style="font-size:10pt;font-weight:600;margin:0 0 8px 0;">${title}</h4>
    <p style="font-size:10pt;white-space:pre-wrap;margin:0;">{{hotelMessage}}</p>
  </div>
  <div style="height: 28px;"></div>
  {{/if}}
</div>`;

  const existingIdx = offerInput.contentBlocks.findIndex(cb => (cb.template || '').includes('{{hotelMessage}}'));
  if (existingIdx >= 0) {
    offerInput.contentBlocks[existingIdx].template = template;
    return;
  }

  const block: OfferTemplateContentBlock = {
    type: 'PLAIN_HTML',
    template,
    sequence: 0,
    blockDescription: null,
    formFields: [],
    lineItems: [],
  };

  const cancellationIdx = offerInput.contentBlocks.findIndex(
    cb =>
      String(cb.type) === 'CANCELLATION_ITEMS' ||
      (cb.template || '').includes('cancellationRules') ||
      (cb.template || '').includes('cancellation-section'),
  );
  if (cancellationIdx >= 0) {
    offerInput.contentBlocks.splice(cancellationIdx, 0, block);
    return;
  }

  const hotelMsgIdx = offerInput.contentBlocks.findIndex(cb => (cb.template || '').includes('personalMessage'));
  if (hotelMsgIdx >= 0) {
    offerInput.contentBlocks.splice(hotelMsgIdx + 1, 0, block);
    return;
  }

  offerInput.contentBlocks.push(block);
}

export function applyTemplateToInput<T extends OfferTemplateInput & { title?: string | null; filename?: string | null }>(spaceId: number, template: IOfferTemplate, input: T): T {
  const result = _.cloneDeep(input);
  result.pageOptions = {
    marginTop: template.marginTop,
    marginBottom: template.marginBottom,
    marginLeft: template.marginLeft,
    marginRight: template.marginRight,
    cssStyles: template.cssStyles,
    headerTemplate: template.headerTemplate,
    footerTemplate: template.footerTemplate,
  };

  const offerTemplateInputFormatted = applyFormattingToInput(spaceId, input);
  if (template.title) {
    result.title = handlebarsSync(template.title, offerTemplateInputFormatted);
  }
  if (template.filename) {
    result.filename = handlebarsSync(template.filename, offerTemplateInputFormatted);
  }
  result.includeEmptyLineItems = template.includeEmptyLineItems;

  const inputContentBlocks = input.contentBlocks;
  result.contentBlocks = [];

  let contentBlockSequence = 1;
  for (const templateContentBlock of template.contentBlocks) {
    if (templateContentBlock.type === 'LINE_ITEMS') {
      const inputLineItemsContentBlocks = inputContentBlocks.filter(cb => cb.type === 'LINE_ITEMS');
      for (const inputLineItemsContentBlock of inputLineItemsContentBlocks) {
        result.contentBlocks.push({
          ..._.cloneDeep(inputLineItemsContentBlock),
          template: templateContentBlock.template,
          sequence: contentBlockSequence++,
        });
      }
    } else if (templateContentBlock.type === 'CANCELLATION_ITEMS') {
      const inputCancellationItemsContentBlocks = inputContentBlocks.filter(cb => cb.type === 'CANCELLATION_ITEMS');
      for (const inputCancellationItemsContentBlock of inputCancellationItemsContentBlocks) {
        result.contentBlocks.push({
          ..._.cloneDeep(inputCancellationItemsContentBlock),
          template: templateContentBlock.template,
          sequence: contentBlockSequence++,
        });
      }
    } else {
      //TODO FormFields fehlen ?
      result.contentBlocks.push({
        ..._.cloneDeep(templateContentBlock),
        sequence: contentBlockSequence++,
      });
    }
  }
  return result;
}

function isCancellationItemsBlock(cb: OfferTemplateContentBlockFormatted): boolean {
  return String(cb.type) === 'CANCELLATION_ITEMS';
}

/** Cancellation fee line items (not the policy text block). */
function isCancellationFeeBlock(cb: OfferTemplateContentBlockFormatted): boolean {
  if (!isCancellationItemsBlock(cb)) {
    return false;
  }
  return (cb.lineItems || []).some(li => li.priceNet !== NO_PRICE && li.priceNet !== 0);
}

/**
 * DB template is the standard offer Storno Handlebars (epilo samples / variants).
 */
function blockUsesOfferCancellationPolicyTemplate(template: string | null | undefined): boolean {
  if (!template) {
    return false;
  }
  return (
    template.includes('{{#each cancellationRules}}') ||
    template.includes('{{#if cancellationRulesUseGuestCategories}}') ||
    template.includes('cancellationRulesOvernight') ||
    template.includes('cancellationRulesSeminar') ||
    template.includes('cancellationRules.0.toleranceRateFormatted') ||
    template.includes('cancellationToleranceRateWholeFormatted') ||
    (template.includes('cancellationRules') && template.includes('bei Stornierung'))
  );
}

/**
 * Replace this block with `buildBookingsPageCancellationPolicyHtml` output: any non-fee CANCELLATION_ITEMS
 * (legacy templates often fail marker checks or carry 0 € rows), or Storno PLAIN_HTML / marker templates.
 */
function shouldReplaceWithBookingsCancellationPolicy(cb: OfferTemplateContentBlockFormatted): boolean {
  if (isCancellationFeeBlock(cb)) {
    return false;
  }
  if (blockUsesOfferCancellationPolicyTemplate(cb.template)) {
    return true;
  }
  if (isCancellationItemsBlock(cb)) {
    return true;
  }
  if (String(cb.type) === 'PLAIN_HTML' && cb.template?.includes('cancellationRules')) {
    return true;
  }
  return false;
}

function getEffectiveCancellationPolicyHtml(input: OfferTemplateInputFormatted): string {
  if (!input.cancellationRules?.length) {
    return '';
  }
  const rules: OfferTemplateCancellationRule[] = input.cancellationRules.map(r => ({
    daysToEvent: r.daysToEvent,
    rate: r.rate,
    toleranceRate: r.toleranceRate,
    minOvernight: r.minOvernight,
    maxOvernight: r.maxOvernight,
    minTotalGuests: r.minTotalGuests,
    maxTotalGuests: r.maxTotalGuests,
  }));
  return buildBookingsPageCancellationPolicyHtml(rules, input.cancellationToleranceRate, input.language).trim();
}

/** Replace legacy DB Handlebars with pre-rendered HTML matching My Bookings (`buildBookingsPageCancellationPolicyHtml`). */
function resolveOfferContentBlockHandlebarsTemplate(
  cb: OfferTemplateContentBlockFormatted,
  blockIndex: number,
  inputFormatted: OfferTemplateInputFormatted,
): string {
  const policyHtml = getEffectiveCancellationPolicyHtml(inputFormatted);
  if (policyHtml === '') {
    return cb.template;
  }
  if (!shouldReplaceWithBookingsCancellationPolicy(cb)) {
    return cb.template;
  }
  const firstIdx = inputFormatted.contentBlocks.findIndex(shouldReplaceWithBookingsCancellationPolicy);
  if (firstIdx === blockIndex) {
    return `<div class="container"><div class="cancellation-section" style="padding-top: 8px;">${policyHtml}</div></div>`;
  }
  if (firstIdx !== -1 && blockIndex > firstIdx && shouldReplaceWithBookingsCancellationPolicy(cb)) {
    return '';
  }
  return cb.template;
}

export async function generateHtmlOffer(inputFormatted: OfferTemplateInputFormatted, resolveMediaSrc?: TResolveMediaSrc | null): Promise<string> {
  const renderedContentBlocks = await Promise.all(
    inputFormatted.contentBlocks.map(async (cb, index) => {
      try {
        const renderedBlock = await handlebars(
          resolveOfferContentBlockHandlebarsTemplate(cb, index, inputFormatted),
          {
            ...inputFormatted,
            contentBlock: {
              ...cb,
              formFields: await cb.formFields?.reduce(
                async (obj, item) => ({
                  ...(await obj),
                  [item.name]: await handlebars(item.defaultValue || item.value || '', inputFormatted, resolveMediaSrc),
                }),
                {},
              ),
            },
            lineItems: cb.lineItems,
            inputDay: cb.inputDay,
            cancellationRule: cb.cancellationRule,
          },
          resolveMediaSrc,
        );
        return renderedBlock;
      } catch (e: any) {
        throw new Error(`Fehler in Block #${cb.sequence}: ` + e.message);
      }
    }),
  );
  return htmlTemplate2Html({
    cssStyles: inputFormatted.pageOptions.cssStyles,
    marginBottom: inputFormatted.pageOptions.marginBottom,
    marginLeft: inputFormatted.pageOptions.marginLeft,
    marginRight: inputFormatted.pageOptions.marginRight,
    marginTop: inputFormatted.pageOptions.marginTop,
    headerTemplate: await handlebars(inputFormatted.pageOptions.headerTemplate || '', inputFormatted, resolveMediaSrc),
    footerTemplate: await handlebars(inputFormatted.pageOptions.footerTemplate || '', inputFormatted, resolveMediaSrc),
    bodyParts: renderedContentBlocks,
  });
}

export async function generateHtmlOfferHeaderForPdf(
  inputFormatted: OfferTemplateInputFormatted,
  resolveMediaSrc?: TResolveMediaSrc | null,
): Promise<string> {
  return await handlebars(inputFormatted.pageOptions.headerTemplate || '', inputFormatted, resolveMediaSrc);
}

export async function generateHtmlOfferFooterForPdf(
  inputFormatted: OfferTemplateInputFormatted,
  resolveMediaSrc?: TResolveMediaSrc | null,
): Promise<string> {
  return await handlebars(inputFormatted.pageOptions.footerTemplate || '', inputFormatted, resolveMediaSrc);
}

export async function generateHtmlOfferForPdf(
  inputFormatted: OfferTemplateInputFormatted,
  resolveMediaSrc?: TResolveMediaSrc | null,
): Promise<string> {
  const renderedContentBlocks = await Promise.all(
    inputFormatted.contentBlocks.map(async (cb, index) => {
      try {
        const renderedBlock = await handlebars(
          resolveOfferContentBlockHandlebarsTemplate(cb, index, inputFormatted),
          {
            ...inputFormatted,
            contentBlock: {
              ...cb,
              formFields: await cb.formFields?.reduce(
                async (obj, item) => ({
                  ...(await obj),
                  [item.name]: await handlebars(item.defaultValue || item.value || '', inputFormatted, resolveMediaSrc),
                }),
                {},
              ),
            },
            lineItems: cb.lineItems,
            inputDay: cb.inputDay,
            cancellationRule: cb.cancellationRule,
          },
          resolveMediaSrc,
        );
        //if (cb.type === "CUSTOM_FORM") console.log("block", JSON.stringify(cb.formFields), cb.template, renderedBlock)
        return renderedBlock;
      } catch (e: any) {
        throw new Error(`Fehler in Block #${cb.sequence}: ` + e.message);
      }
    }),
  );
  return pdfTemplate2Html({
    cssStyles: inputFormatted.pageOptions.cssStyles,
    bodyParts: renderedContentBlocks,
  });
}
